import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	Chip,
	Divider,
	List,
	ListItemButton,
	ListItemText,
	TablePagination,
} from "@mui/material";
import {SearchForm} from "@variocube/app-ui";
import React, {useEffect, useState} from "react";
import {useAsync} from "react-async-hook";
import {Link, useNavigate} from "react-router-dom";
import {Tenant, TenantQuery, useTenantApi} from "../api";
import {FullScreenMessage} from "../controls";
import {Flavor, getFlavor} from "@smalox/shared";
import {useLocalization} from "@smalox/shared";
import {LoginState} from "../LoginState";
import {useUser} from "../user";
import {View, ViewHeader} from "./View";

export function TenantSelection() {
	const {t} = useLocalization();
	const {queryTenants} = useTenantApi();
	const navigate = useNavigate();
	const {superUser} = useUser();
	const currentFlavor = getFlavor();

	const [query, setQuery] = useState<TenantQuery>({size: 10});
	const {loading, error, result} = useAsync(queryTenants, [query]);

	const [initialLoading, setInitialLoading] = useState(true);

	useEffect(() => {
		if (!loading) {
			setInitialLoading(false);
		}
	}, [loading]);

	const [tenants, setTenants] = useState<Tenant[]>([]);
	useEffect(() => {
		if (result) {
			setTenants(result?.content ?? []);
		}
	}, [result]);

	// Automatically navigate to tenant, if there is only one and search is not active
	useEffect(() => {
		if (result?.content && !query.search && result.totalElements === 1) {
			navigate(`/${result.content[0].id}`);
		}
	}, [result, query]);

	if (!query.search && result?.totalElements === 0) {
		return (
			<FullScreenMessage>
				<Alert severity="error">
					<AlertTitle>{t("tenants.selection.noTenants.title")}</AlertTitle>
					{t("tenants.selection.noTenants.description")}
				</Alert>
			</FullScreenMessage>
		);
	}

	return (
		<LoginState loading={initialLoading} error={error}>
			<Box py={4}>
				<View maxWidth="md">
					<ViewHeader
						title={t("tenants.selection.title")}
						actions={superUser && (
							<Button variant="outlined" color="primary" component={Link} to="new">
								{t("tenants.create.title")}
							</Button>
						)}
					/>
					<SearchForm
						label={t("filter.search")}
						fullWidth
						onSearch={search => setQuery({...query, search, page: 0})}
					/>
					<Card>
						<List>
							{tenants.map((tenant) => {
								const tenantFlavor = (tenant.flavor ?? "SMALOX") as Flavor;
								const showFlavorChip = tenantFlavor !== currentFlavor;
								return (
									<ListItemButton
										key={tenant.id}
										component={Link}
										to={`/${tenant.id}`}
										disabled={loading}
									>
										<ListItemText
											primary={
												<Box display="flex" alignItems="center" gap={1}>
													{tenant.name}
													{showFlavorChip && (
														<Chip
															label={t(`flavor.${tenantFlavor}`)}
															size="small"
															variant="outlined"
														/>
													)}
												</Box>
											}
										/>
									</ListItemButton>
								);
							})}
						</List>
						<Divider />
						<TablePagination
							component="div"
							rowsPerPageOptions={[10]}
							count={result?.totalElements ?? 0}
							rowsPerPage={10}
							page={result?.number ?? 0}
							onPageChange={(_, page) => setQuery({...query, page})}
							onRowsPerPageChange={() => void 0}
						/>
					</Card>
				</View>
			</Box>
		</LoginState>
	);
}
